<template lang="pug">
div(v-if="page?.block")
  Header(
    :header="page?.header"
  )
  |
  div(
    v-for="(block, index) in page.block"
    :key="index"
    :id="block?.anchor || `${index}-${block.__typename}`"
  )
    component(
      :is="block.__typename"
      :key="`${index}-${block.__typename}`"
      :block="block"
      :ref="() => { if (block.popupId && block.__typename === 'ComponentSharedPopup') popupRefs.push(block.popupId) }"
      :id="block.__typename !== 'ComponentSharedPopup' && block?.popupId ? block.popupId : `component-${index}-${block.__typename}`"
      v-model="isPopupOpen"
    )
  |
  div(v-if="page?.seo")
    ComponentSharedSeo(
      :seo="page.seo"
    )
  |
  Footer(
    :footer="page?.footer"
  )
</template>

<script setup lang="ts">
import { useAsyncData } from '#app';

const route = useRoute();

const slug = route.params.slug as string || "";

const { data: page } = await useAsyncData(`page-${slug}`, async () => {
  const pages = await queryCollection("pages").all();

  if (!pages) return null;

  const pSlug = slug ? `/${slug}` : "/";

  return pages[0]?.meta?.body?.find((p) => p.slug === pSlug) || null;
});

const headerOffset = 82;

// --- Scroll to anchor ---
const adjustScrollForHeader = () => {
  let { hash } = window.location;

  // --- Remove trailing slash in hash if present ---
  if (hash && hash.endsWith('/') && hash.length > 1) {
    const cleanedHash = hash.slice(0, -1);
    const baseUrl = window.location.href.split('#')[0];
    history.replaceState(null, '', baseUrl + cleanedHash); // no reload
    hash = cleanedHash;
  }

  if (!hash) return;

  const targetElement = document.querySelector(hash);

  if (!targetElement) return;

  setTimeout(() => {
    const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: elementPosition,
      behavior: "smooth",
    });
  }, 100);
};

onMounted(() => {
  adjustScrollForHeader();
});

watch(() => route.hash, () => {
  adjustScrollForHeader();
});

// --- Popup ---
const popupRefs = ref<string[]>([]);

const isPopupOpen = ref(false);
const hasOpenedPopup = ref(false);

const handleScroll = () => {
  const scrollTop = window.scrollY;
  const windowHeight = window.innerHeight;
  const scrollPosition = scrollTop + windowHeight;

  // --- Case 1: popupId is provided ---
  if (popupRefs.value?.length) {
    const targetId = popupRefs.value[0];
    const targetElement = document.getElementById(targetId);

    if (!targetElement) return;

    const elementMiddle = targetElement.getBoundingClientRect().top + window.scrollY + targetElement.offsetHeight / 2;
    const viewportMiddle = window.scrollY + window.innerHeight / 2;

    if (viewportMiddle >= elementMiddle && !hasOpenedPopup.value) {
      const storageKey = `popup-opened-${route.path}-${targetId}`;
      if (localStorage.getItem(storageKey) === "true") return;

      isPopupOpen.value = true;
      hasOpenedPopup.value = true;
      localStorage.setItem(storageKey, "true");
    }

    return;
  }

  // --- Case 2: default if no popupId (50% scroll) ---
  const docHeight = document.body.scrollHeight;
  const triggerPoint = docHeight * 0.5;

  if (scrollPosition >= triggerPoint && !hasOpenedPopup.value) {
    const storageKey = `popup-opened-${route.path}-default`;
    if (localStorage.getItem(storageKey) === "true") return;

    isPopupOpen.value = true;
    hasOpenedPopup.value = true;
    localStorage.setItem(storageKey, "true");
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

watch(() => route.fullPath, () => {
  isPopupOpen.value = false;
});
</script>